import React, { FC, useEffect, useState } from 'react';
import { Button, Icon, Message } from 'semantic-ui-react';
import { useCentralCalls } from '@src/rest/CentralCalls';

interface RelatedWorkProps {
    clientId: string;
    offeringId: string;
}

interface RelatedWorkItem {
    ClientName: string;
    ClientId:string;
    DocumentName: string;
    DocumentType: string;
    DocumentId: string;
    DocumentVersionId: string;
    VersionNumber: number;
    CompletedByConsultantName: string;
    CompletedDate: string;
}

const getCaseInsensitiveValue = <T = unknown>( obj: unknown, key: string ): T | undefined => {
    if( !obj || typeof obj !== 'object' ) return undefined;
    const record = obj as Record<string, unknown>;
    const foundKey = Object.keys( record ).find( k => k.toLowerCase() === key.toLowerCase() );
    return foundKey ? ( record[ foundKey ] as T ) : undefined;
};

const asString = ( value: unknown ): string => `${ value ?? '' }`.trim();

const asNumber = ( value: unknown ): number => {
    if( typeof value === 'number' ) return value;
    const parsed = Number( value );
    return Number.isNaN( parsed ) ? 0 : parsed;
};

const parseArrayPayload = ( response: any ): any[] => {
    if( Array.isArray( response ) ) return response;

    const candidates = [ response?.result, response?.Result, response?.data, response?.Data ];
    for( const candidate of candidates ){
        if( Array.isArray( candidate ) ) return candidate;
        if( typeof candidate === 'string' ){
            try {
                const parsed = JSON.parse( candidate );
                if( Array.isArray( parsed ) ) return parsed;
            } catch {
                // ignore malformed JSON wrappers
            }
        }
    }

    if( typeof response === 'string' ){
        try {
            const parsed = JSON.parse( response );
            if( Array.isArray( parsed ) ) return parsed;
        } catch {
            // ignore malformed JSON payload
        }
    }

    return [];
};

const normalizeRelatedWork = ( payload: any[] ): RelatedWorkItem[] =>
    payload
        .map( row => ( {
            ClientName: asString( getCaseInsensitiveValue( row, 'ClientName' ) ),
            ClientId: asString( getCaseInsensitiveValue( row, 'ClientId' ) ),
            DocumentName: asString( getCaseInsensitiveValue( row, 'DocumentName' ) ),
            DocumentType: asString( getCaseInsensitiveValue( row, 'DocumentType' ) ),
            DocumentId: asString( getCaseInsensitiveValue( row, 'DocumentId' ) ),
            DocumentVersionId: asString( getCaseInsensitiveValue( row, 'DocumentVersionId' ) ),
            VersionNumber: asNumber( getCaseInsensitiveValue( row, 'VersionNumber' ) ),
            CompletedByConsultantName: asString( getCaseInsensitiveValue( row, 'CompletedByConsultantName' ) ),
            CompletedDate: asString( getCaseInsensitiveValue( row, 'CompletedDate' ) ),
        } ) )
        .filter( item => !!item.DocumentId && !!item.DocumentName );

const RelatedWork: FC<RelatedWorkProps> = ( { clientId, offeringId } ) => {
    const { executeStoredProcedure, downloadDocumentFromStorage } = useCentralCalls();

    const [ rows, setRows ]                                   = useState<RelatedWorkItem[]>( [] ),
          [ isLoading, setIsLoading ]                         = useState( true ),
          [ errorMessage, setErrorMessage ]                   = useState( '' ),
          [ downloadingDocumentId, setDownloadingDocumentId ] = useState( '' );

    useEffect( () => {
        if( !clientId || !offeringId ){
            setRows( [] );
            setIsLoading( false );
            setErrorMessage( '' );
            return;
        }

        let cancelled = false;

        const load = async () => {
            setIsLoading( true );
            setErrorMessage( '' );

            try {
                const response = await executeStoredProcedure( 'tng.sp_get_RelatedWorkContent',   { offeringId, clientId:clientId}  );
                const payload = parseArrayPayload( response );
                console.log('params for RELATEEDWORK', { offeringId, clientId:clientId } );
                console.log( '[RelatedWork] Loaded related work response:', response );
                const first = payload[ 0 ];
                const nestedJson = typeof first?.JSON === 'string'
                    ? parseArrayPayload( first.JSON )
                    : null;

                const normalized = normalizeRelatedWork( nestedJson && nestedJson.length > 0 ? nestedJson : payload );
                if( !cancelled ){
                    setRows( normalized );
                }
            } catch( err ){
                if( !cancelled ){
                    const message = err instanceof Error ? err.message : 'Failed to load related work.';
                    setErrorMessage( message );
                    setRows( [] );
                }
            } finally {
                if( !cancelled ){
                    setIsLoading( false );
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
        // NOTE: executeStoredProcedure can be a new function reference each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ clientId, offeringId ] );

    const handleDownload = async ( row: RelatedWorkItem ) => {
        if( !clientId || !row.DocumentId || downloadingDocumentId ) return;

        setDownloadingDocumentId( row.DocumentId );
        setErrorMessage( '' );

        try {
            const blob = await downloadDocumentFromStorage( row.ClientId, row.DocumentId );
            const extension = row.DocumentType ? `.${ row.DocumentType }` : '';
            const suggestedName = `${ row.DocumentName || 'related-work' }${ extension }`;

            const url = URL.createObjectURL( blob );
            const anchor = document.createElement( 'a' );
            anchor.href = url;
            anchor.download = suggestedName;
            document.body.appendChild( anchor );
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL( url );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to download document.';
            setErrorMessage( message );
        } finally {
            setDownloadingDocumentId( '' );
        }
    };

    return (
        <div className={ 'related-work-panel' }>
            <div className={ 'related-work-title' }>Related Work</div>

            { errorMessage && (
                <Message
                    negative
                    icon={ 'warning circle' }
                    className={ 'document-offering-management-error' }
                    content={ errorMessage }
                />
            ) }

            { isLoading && (
                <div className={ 'document-offering-management-empty' }>
                    <Icon name={ 'spinner' } loading /> Loading related work...
                </div>
            ) }

            { !isLoading && rows.length === 0 && !errorMessage && (
                <div className={ 'document-offering-management-empty' }>
                    No related work found.
                </div>
            ) }

            { !isLoading && rows.length > 0 && (
                <div className={ 'related-work-list' }>
                    { rows.map( row => {
                        const downloadBusy = downloadingDocumentId === row.DocumentId;
                        return (
                            <div key={ `${ row.DocumentVersionId || row.DocumentId }` } className={ 'related-work-row' }>
                                <div className={ 'related-work-row-main' }>
                                    <div className={ 'related-work-row-title' }>
                                        { row.DocumentName }
                                        { row.DocumentType && (
                                            <span className={ 'related-work-row-ext' }>.{ row.DocumentType }</span>
                                        ) }
                                    </div>
                                    <div className={ 'related-work-row-meta' }>
                                        <span>Client: { row.ClientName || 'Unknown' }</span>
                                        <span>Consultant: { row.CompletedByConsultantName || 'Unknown' }</span>
                                        { row.VersionNumber > 0 && <span>Version { row.VersionNumber }</span> }
                                        { row.CompletedDate && <span>{ row.CompletedDate }</span> }
                                    </div>
                                </div>

                                <Button
                                    type={ 'button' }
                                    className={ 'related-work-download-btn' }
                                    icon
                                    title={ 'Download document' }
                                    aria-label={ 'Download document' }
                                    loading={ downloadBusy }
                                    disabled={ downloadBusy }
                                    onClick={ () => handleDownload( row ) }
                                >
                                    <Icon name={ 'download' } />
                                </Button>
                            </div>
                        );
                    } ) }
                </div>
            ) }
        </div>
    );
};

export default RelatedWork;